'use client';
import React from 'react';
import { Map, QrCode, GitBranch, Cpu, Satellite, AlertTriangle, BarChart2, TrendingUp, FileText, Gavel, Camera, Search, Plus, Wind } from 'lucide-react';
import type { ActivePanel } from './GISPropertyHubClient';
import Icon from '@/components/ui/AppIcon';


interface GISHubHeaderProps {
  activePanel: ActivePanel;
  onPanelChange: (panel: ActivePanel) => void;
  onAddProperty: () => void;
}

const tabs: { id: ActivePanel; label: string; icon: React.ElementType; badge?: string }[] = [
  { id: 'map', label: 'Satellite Map', icon: Map },
  { id: 'parcels', label: 'QR Registry', icon: QrCode, badge: '4,821' },
  { id: 'ownership', label: 'Ownership', icon: GitBranch },
  { id: 'ai-discovery', label: 'AI Discovery', icon: Cpu, badge: '37' },
  { id: 'valuation', label: 'Valuation', icon: TrendingUp },
  { id: 'tax-bills', label: 'Tax Bills', icon: FileText },
  { id: 'delinquency', label: 'Delinquency', icon: AlertTriangle, badge: '6' },
  { id: 'inspections', label: 'Inspections', icon: Camera },
  { id: 'enforcement', label: 'Enforcement', icon: Gavel },
  { id: 'performance', label: 'Performance', icon: BarChart2 },
  { id: 'pipeline', label: 'Unregistered', icon: Search, badge: '6' },
  { id: 'drone', label: 'Drone Mapping', icon: Wind, badge: 'NEW' },
];

const stats = [
  { label: 'Registered Parcels', value: '4,821', color: 'var(--accent)' },
  { label: 'Unregistered Detected', value: '312', color: '#f87171' },
  { label: 'Counties Mapped', value: '15 / 15', color: '#34d399' },
  { label: 'Pending Valuation', value: '189', color: '#fbbf24' },
];

export default function GISHubHeader({ activePanel, onPanelChange, onAddProperty }: GISHubHeaderProps) {
  return (
    <div className="mb-5">
      {/* Title row */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-3">
          <div
            className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)' }}
          >
            <Satellite size={20} style={{ color: '#070e1f' }} />
          </div>
          <div>
            <h1 className="text-lg sm:text-xl font-bold" style={{ color: 'var(--dark-text)' }}>
              GIS Property Management Hub
            </h1>
            <p className="text-xs sm:text-sm" style={{ color: 'var(--dark-muted)' }}>
              Satellite-mapped parcels · Property tax engine · Field enforcement · AI discovery — all 15 Liberian counties
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ backgroundColor: 'rgba(248,113,113,0.15)', color: '#f87171', border: '1px solid rgba(248,113,113,0.3)' }}>
            <AlertTriangle size={12} />
            37 AI Alerts Active
          </div>
          <button
            onClick={onAddProperty}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-bold transition-all hover:opacity-85 active:scale-95"
            style={{ background: 'linear-gradient(135deg, #b8860b 0%, #d4a017 100%)', color: '#070e1f', boxShadow: '0 2px 12px rgba(212,160,23,0.35)' }}
          >
            <Plus size={14} />
            Add Property
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        {stats.map((s) => (
          <div
            key={s.label}
            className="rounded-lg px-4 py-3 flex flex-col gap-0.5"
            style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}
          >
            <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>{s.label}</span>
            <span className="text-lg font-bold font-mono-data" style={{ color: s.color }}>{s.value}</span>
          </div>
        ))}
      </div>

      {/* Tabs — scrollable */}
      <div
        className="flex gap-1 p-1 rounded-lg overflow-x-auto"
        style={{ backgroundColor: 'var(--dark-surface)', border: '1px solid var(--dark-border)' }}
      >
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const active = activePanel === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => onPanelChange(tab.id)}
              className="flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-medium transition-all duration-150 flex-shrink-0 whitespace-nowrap"
              style={{
                backgroundColor: active ? 'var(--dark-elevated)' : 'transparent',
                color: active ? 'var(--accent)' : 'var(--dark-muted)',
                border: active ? '1px solid var(--dark-border)' : '1px solid transparent',
              }}
            >
              <Icon size={13} />
              {tab.label}
              {tab.badge && (
                <span
                  className="text-xs px-1.5 py-0.5 rounded-full font-semibold"
                  style={{ backgroundColor: 'rgba(212,160,23,0.2)', color: 'var(--accent)' }}
                >
                  {tab.badge}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
